import * as React from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import useMediaQuery from '@mui/material/useMediaQuery';
import Paper from '@mui/material/Paper';
import { TablePaginationActions } from '../../../components/Pagination/TablePaginationActions';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useNavigate } from 'react-router-dom';
import HomeWork from '@mui/icons-material/HomeWork';
import { MENU_OPEN } from 'store/actions';
import { destroy, get, getExcel } from 'services/ura';
import { Autocomplete, Box, Button, Grid, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import PlaylistAddCheck from '@mui/icons-material/PlaylistAddCheck';
import DeleteIcon from '@mui/icons-material/Delete';
import Excel from '@mui/icons-material/Task';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import AnimateButton from 'ui-component/extended/AnimateButton';
import ModalFilter from 'components/Modal/ModalFilter';
import { getUnit } from 'services/unit';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#c0c0c0',
        color: theme.palette.common.white
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14
    }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
    },
    '&:last-child td, &:last-child th': {
        border: 0
    }
}));

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 480,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4
};

const STATUS_OPTIONS = [
    { id: '', label: 'Todas' },
    { id: 'pendente', label: 'Pendentes' },
    { id: 'cancelada', label: 'Canceladas' },
    { id: 'convertida', label: 'Convertidas em OS' }
];

export default function GridUra() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const page = useSelector((state) => state.ura.page);
    const rowsPerPage = useSelector((state) => state.ura.rowsPerPage);
    const [uras, setUras] = React.useState([]);
    const [success, setSuccess] = React.useState('');
    const [error, setError] = React.useState('');
    const [idDestroy, setIdDestroy] = React.useState('');
    const [openModal, setOpenModal] = React.useState(false);
    const [motivoExclusao, setMotivoExclusao] = React.useState('');
    const [motivoError, setMotivoError] = React.useState('');
    const [openModalReport, setOpenModalReport] = React.useState(false);
    const [units, setUnits] = React.useState([]);
    const [filterUnit, setFilterUnit] = React.useState(null);
    const [filterStatus, setFilterStatus] = React.useState(STATUS_OPTIONS[0]);
    const [filterInitialDate, setFilterInitialDate] = React.useState(null);
    const [filterFinalDate, setFilterFinalDate] = React.useState(null);
    const [loadingExcel, setLoadingExcel] = React.useState(false);

    const formatDateTimeExact = (value, formatted) => {
        if (formatted) return formatted;
        if (!value) return '-';

        const date = new Date(value);
        if (!Number.isNaN(date.getTime())) {
            return new Intl.DateTimeFormat('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            })
                .format(date)
                .replace(',', '');
        }

        const match = String(value).match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/);
        if (!match) return value;
        const [, year, month, day, hour, minute] = match;
        return `${day}/${month}/${year} ${hour}:${minute}`;
    };

    const handleChangePage = (event, newPage) => {
        dispatch({ type: 'SET_PAGE_URA', payload: newPage });
    };
    const handleChangeRowsPerPage = (event) => {
        dispatch({ type: 'SET_ROWS_PER_PAGE_URA', payload: parseInt(event.target.value, 10) });
        dispatch({ type: 'SET_PAGE_URA', payload: 0 });
    };

    React.useEffect(() => {
        getAllUras();
    }, [page, rowsPerPage]);

    React.useEffect(() => {
        getUnit()
            .then((resp) => {
                const list = Array.isArray(resp.data) ? resp.data : resp.data?.data || [];
                setUnits(list.map((u) => ({ id: u.id, label: u.nome || u.label })));
            })
            .catch(() => setUnits([]));
    }, []);

    React.useEffect(() => {
        const currentIndex = document.location.pathname
            .toString()
            .split('/')
            .findIndex((id) => id === 'uras');
        if (currentIndex > -1) {
            dispatch({ type: MENU_OPEN, id: 'uras' });
        }
    }, []);

    function getAllUras() {
        get(page + 1, rowsPerPage).then((resp) => setUras(resp.data));
    }

    const handleOpenDestroy = (id) => {
        setIdDestroy(id);
        setMotivoExclusao('');
        setMotivoError('');
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setMotivoExclusao('');
        setMotivoError('');
        setIdDestroy('');
    };

    const deleteById = () => {
        const motivo = motivoExclusao.trim();
        if (!motivo) {
            setMotivoError('Informe o motivo da exclusão.');
            return;
        }

        destroy(idDestroy, motivo)
            .then((resp) => {
                handleCloseModal();
                getAllUras();
                setError('');
                setSuccess(resp.data.success);
                setTimeout(() => setSuccess(''), 3000);
            })
            .catch((e) => {
                setSuccess('');
                setError(e?.response?.data?.error || 'Não foi possível excluir o registro.');
                setTimeout(() => setError(''), 4000);
            });
    };

    const handleUra = (row) => {
        const unit = row?.unidade;
        const family = row?.familia;
        const type_os = row?.tipo_os;
        const payload = JSON.parse(row?.payload);
        const firstDigit = payload?.type_ikey[0];

        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_UNIT_USER', payload: unit?.id });
        dispatch({ type: 'SET_FAMILY_URA', payload: family ? { ...family, label: family?.nome } : '' });
        dispatch({ type: 'SET_TYPEOS_URA', payload: type_os ? { ...type_os, label: type_os?.nome } : '' });
        dispatch({ type: 'SET_ID_URA', payload: row.id });
        localStorage.setItem('unit', unit?.id);
        setTimeout(() => {
            dispatch({ type: 'SET_LOADING', payload: false });
            if (firstDigit == 4) {
                navigate({ pathname: `/nova_proativa` });
            } else {
                navigate({ pathname: `/nova_corretiva` });
            }
        }, 500);
    };

    const formatFilterDate = (value) => {
        if (!value) return '';
        try {
            return format(value, 'yyyy-MM-dd HH:mm:ss');
        } catch (e) {
            return '';
        }
    };

    const downloadExcel = () => {
        setLoadingExcel(true);
        getExcel(
            filterUnit?.id || '',
            filterStatus?.id || '',
            formatFilterDate(filterInitialDate),
            formatFilterDate(filterFinalDate)
        )
            .then((resp) => {
                const blob = new Blob([resp.data], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const link = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.download = `relatorio_ura_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.xlsx`;
                a.href = link;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(link);
                setOpenModalReport(false);
                setSuccess('Relatório gerado com sucesso.');
                setTimeout(() => setSuccess(''), 3000);
            })
            .catch(() => {
                setError('Não foi possível gerar o relatório.');
                setTimeout(() => setError(''), 4000);
            })
            .finally(() => setLoadingExcel(false));
    };

    return (
        <>
            <Modal open={openModal} onClose={handleCloseModal} aria-labelledby="modal-excluir-ura">
                <Box sx={modalStyle}>
                    <Typography id="modal-excluir-ura" variant="h6" component="h2">
                        Excluir Registro da URA
                    </Typography>
                    <Typography sx={{ mt: 2, mb: 2 }}>
                        Informe o motivo da exclusão do registro #{idDestroy}.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Motivo da exclusão"
                        value={motivoExclusao}
                        onChange={(e) => {
                            setMotivoExclusao(e.target.value);
                            if (e.target.value.trim()) setMotivoError('');
                        }}
                        error={Boolean(motivoError)}
                        helperText={motivoError}
                        required
                    />
                    <Grid container alignItems="end" justifyContent="end" sx={{ mt: 3 }}>
                        <Grid item>
                            <Box sx={{ mt: 2, mr: 3 }}>
                                <AnimateButton>
                                    <Button
                                        disableElevation
                                        size="medium"
                                        type="button"
                                        variant="contained"
                                        color="error"
                                        onClick={handleCloseModal}
                                    >
                                        Cancelar
                                    </Button>
                                </AnimateButton>
                            </Box>
                        </Grid>
                        <Grid item>
                            <Box sx={{ mt: 2, mr: 3 }}>
                                <AnimateButton>
                                    <Button
                                        disableElevation
                                        size="medium"
                                        type="button"
                                        variant="contained"
                                        color="primary"
                                        onClick={deleteById}
                                    >
                                        Confirmar exclusão
                                    </Button>
                                </AnimateButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>

            <ModalFilter
                width="60%"
                open={openModalReport}
                title="Relatório URA"
                handleClose={() => setOpenModalReport(false)}
                content={
                    <>
                        <Grid container spacing={matchDownSM ? 0 : 2}>
                            <Grid item xs={12} sm={6} sx={{ marginTop: 3 }}>
                                <Autocomplete
                                    options={units}
                                    value={filterUnit}
                                    onChange={(e, newValue) => setFilterUnit(newValue)}
                                    renderInput={(params) => <TextField {...params} label="CS" />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ marginTop: 3 }}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Situação"
                                    value={filterStatus?.id ?? ''}
                                    onChange={(e) =>
                                        setFilterStatus(
                                            STATUS_OPTIONS.find((opt) => opt.id === e.target.value) || STATUS_OPTIONS[0]
                                        )
                                    }
                                >
                                    {STATUS_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.id || 'all'} value={opt.id}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ marginTop: 3 }}>
                                <LocalizationProvider locale={ptBR} dateAdapter={AdapterDateFns}>
                                    <Stack spacing={3}>
                                        <DateTimePicker
                                            ampm={false}
                                            label="Data/Hora Inicial"
                                            value={filterInitialDate}
                                            onChange={(value) => setFilterInitialDate(value)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </Stack>
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ marginTop: 3 }}>
                                <LocalizationProvider locale={ptBR} dateAdapter={AdapterDateFns}>
                                    <Stack spacing={3}>
                                        <DateTimePicker
                                            ampm={false}
                                            label="Data/Hora Final"
                                            value={filterFinalDate}
                                            onChange={(value) => setFilterFinalDate(value)}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </Stack>
                                </LocalizationProvider>
                            </Grid>
                        </Grid>
                        <Grid container alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
                            <Grid item>
                                <Box sx={{ mt: 2, mr: 3 }}>
                                    <AnimateButton>
                                        <Button
                                            disableElevation
                                            size="large"
                                            type="button"
                                            variant="contained"
                                            color="error"
                                            onClick={() => setOpenModalReport(false)}
                                        >
                                            Cancelar
                                        </Button>
                                    </AnimateButton>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ mt: 2, mr: 3 }}>
                                    <AnimateButton>
                                        <Button
                                            disableElevation
                                            size="large"
                                            type="button"
                                            variant="contained"
                                            color="primary"
                                            startIcon={<Excel />}
                                            disabled={loadingExcel}
                                            onClick={downloadExcel}
                                        >
                                            {loadingExcel ? 'Gerando...' : 'Gerar Excel'}
                                        </Button>
                                    </AnimateButton>
                                </Box>
                            </Grid>
                        </Grid>
                    </>
                }
            />

            <TableContainer sx={{ mt: 2, height: '100%', padding: 3 }} component={Paper}>
                <Grid container alignItems="center" justifyContent="space-between">
                    <Grid item>
                        <h1
                            style={{
                                font: 'normal normal bold 35px/44px Myriad Pro',
                                letterSpacing: '0px',
                                color: 'black',
                                opacity: 1,
                                padding: 15,
                                marginLeft: '2%'
                            }}
                        >
                            <HomeWork /> URA
                        </h1>
                    </Grid>
                    <Grid item sx={{ pr: 3 }}>
                        <AnimateButton>
                            <Button
                                disableElevation
                                variant="contained"
                                color="primary"
                                startIcon={<Excel />}
                                onClick={() => setOpenModalReport(true)}
                            >
                                Relatório
                            </Button>
                        </AnimateButton>
                    </Grid>
                </Grid>
                <hr style={{ width: '95%', marginTop: 0 }}></hr>
                <h3
                    style={{
                        font: 'normal normal 300 18px/22px Myriad Pro',
                        letterSpacing: '0px',
                        color: 'black',
                        opacity: 1,
                        padding: 15,
                        marginLeft: '2%'
                    }}
                >
                    Gerencie suas ligaçoes
                </h3>
                {error || success ? (
                    <Snackbar open={true} autoHideDuration={6000}>
                        <Alert
                            severity={error ? 'error' : success ? 'success' : ''}
                            sx={{
                                width: '100%',
                                backgroundColor: error ? 'red' : success ? 'green' : 'orange',
                                color: '#FFF'
                            }}
                        >
                            {error ? error : success ? success : ''}
                        </Alert>
                    </Snackbar>
                ) : (
                    ''
                )}
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                    <TableHead style={{ backgroundColor: '#00008B' }}>
                        <TableRow>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Código</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Data/Hora</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Unidade</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Família</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Tipo OS</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Chave URA</StyledTableCell>
                            <StyledTableCell style={{ backgroundColor: '#c0c0c0', color: 'black' }}>Ações</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {uras.data &&
                            uras.data.map((row) => (
                                <StyledTableRow key={row.id}>
                                    <StyledTableCell style={{ color: 'black' }}>{row.id}</StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>
                                        {formatDateTimeExact(row.created_at, row.data_hora)}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>{row?.unidade?.nome}</StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>{row?.familia?.nome}</StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>{row?.tipo_os?.nome}</StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>
                                        {row?.payload
                                            ? JSON.parse(row.payload)?.project_ikey || 'Valor não disponível'
                                            : 'Valor não disponível'}
                                    </StyledTableCell>
                                    <StyledTableCell style={{ color: 'black' }}>
                                        <Tooltip title="Abrir Chamado">
                                            <IconButton onClick={() => handleUra(row)}>
                                                <PlaylistAddCheck />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Excluir">
                                            <IconButton onClick={() => handleOpenDestroy(row.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, { label: 'Todos', value: -1 }]}
                                colSpan={3}
                                count={uras.total}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'Registros por Página'
                                    },
                                    native: true
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </>
    );
}
